import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { buildListParams, type DataGridQuery, type PageRequest } from "@/hooks/use-data-grid";
import { buInstanceKeys } from "@/lib/query";

/**
 * TanStack Query hooks for `bu_instance` (Coach Manager BU connection
 * configs). Mirrors `src/examples/widget/use-widgets.ts`'s pattern. Note:
 * `BuInstance` never carries a password field — no endpoint returns one, even
 * to admins (see the Fleet Relay build plan's Phase 2 credential-handling
 * decision).
 */

export interface BuInstance {
  id: string;
  name: string;
  endpointUrl: string;
  username: string;
  companyId: string | null;
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

interface BuInstancesListResponse {
  data: BuInstance[];
  nextCursor: string | null;
  prevCursor: string | null;
  hasNext: boolean;
  hasPrev: boolean;
  total: number;
}

export interface BuInstanceInput {
  name: string;
  endpointUrl: string;
  username: string;
  /** Required on create. On update, omit (or leave undefined) to keep the existing credential. */
  password?: string;
  companyId?: string;
  active: boolean;
}

export type TestConnectionResult =
  | { ok: true; sessionKey: string; result: string; resultDescription: string }
  | { ok: false; error: string; raw?: string };

export interface TestConnectionInput {
  endpointUrl: string;
  username: string;
  password: string;
}

async function extractErrorMessage(res: Response, fallback: string): Promise<string> {
  const json = await res.json().catch(() => null);
  return json?.error ?? fallback;
}

export function useBuInstances(query: DataGridQuery, pageRequest: PageRequest) {
  return useQuery({
    queryKey: [...buInstanceKeys.list(query), pageRequest] as const,
    queryFn: async (): Promise<BuInstancesListResponse> => {
      const res = await fetch(`/api/admin/bu-instances?${buildListParams(query, pageRequest)}`);
      if (!res.ok) throw new Error(await extractErrorMessage(res, "Failed to fetch BU instances"));
      return res.json();
    },
  });
}

export interface ActiveBuInstance {
  id: string;
  name: string;
  companyId: string | null;
}

/**
 * `{ id, name, companyId }` for every active BU instance — the
 * non-admin-readable picker list (see `listActiveBuInstances` in
 * `src/api/bu-instances.ts`). Not paginated; the full active set is small
 * enough to fetch in one call.
 */
export function useActiveBuInstances() {
  return useQuery({
    queryKey: buInstanceKeys.active,
    queryFn: async (): Promise<ActiveBuInstance[]> => {
      const res = await fetch("/api/bu-instances/active");
      if (!res.ok) throw new Error(await extractErrorMessage(res, "Failed to fetch BU instances"));
      const json = await res.json();
      return json.data;
    },
  });
}

export function useBuInstance(id: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: buInstanceKeys.detail(id),
    queryFn: async (): Promise<BuInstance> => {
      const res = await fetch(`/api/admin/bu-instances/${id}`);
      if (!res.ok) throw new Error(await extractErrorMessage(res, "Failed to fetch BU instance"));
      const json = await res.json();
      return json.data;
    },
    enabled: options?.enabled,
  });
}

export function useCreateBuInstance() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: BuInstanceInput): Promise<BuInstance> => {
      const res = await fetch("/api/admin/bu-instances", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(input),
      });
      if (!res.ok) throw new Error(await extractErrorMessage(res, "Failed to create BU instance"));
      const json = await res.json();
      return json.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: buInstanceKeys.all });
      toast.success("BU instance created");
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : "Failed to create BU instance");
    },
  });
}

export function useUpdateBuInstance(id: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (updates: Partial<BuInstanceInput>): Promise<BuInstance> => {
      const res = await fetch(`/api/admin/bu-instances/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(updates),
      });
      if (!res.ok) throw new Error(await extractErrorMessage(res, "Failed to update BU instance"));
      const json = await res.json();
      return json.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: buInstanceKeys.all });
      toast.success("BU instance updated");
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : "Failed to update BU instance");
    },
  });
}

export function useDeleteBuInstance() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`/api/admin/bu-instances/${id}`, { method: "DELETE" });
      if (!res.ok) throw new Error(await extractErrorMessage(res, "Delete failed"));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: buInstanceKeys.all });
      toast.success("BU instance deleted");
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : "Failed to delete BU instance");
    },
  });
}

/**
 * "Test Connection" probe — not a write, so no cache invalidation. Never
 * throws on a Coach Manager login failure (`{ ok: false, error }` is a valid
 * result the caller renders inline); only throws on a transport/validation
 * failure of the admin route itself (403/400).
 */
export function useTestConnection() {
  return useMutation({
    mutationFn: async (input: TestConnectionInput): Promise<TestConnectionResult> => {
      const res = await fetch("/api/admin/coach-manager/test-connection", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(input),
      });
      if (!res.ok) throw new Error(await extractErrorMessage(res, "Test connection failed"));
      return res.json();
    },
  });
}
